import BoldText from "@base/BoldText";
import RegularText from "@base/RegularText";
import Row from "@base/Row";
import Currency from "@core/currencies/Currency";
import { FiatCurrency } from "@core/fiat/FiatCurrency";
import { colors, settings } from "@styles/globalStyles";
import {
    getPercentagePriceVariationColor,
    getPercentagePriceVariationDaily,
} from "@utils/helpers/currency/currency.helper";
import React, { Component } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { AreaChart } from "react-native-svg-charts";
import PressableBase from "@base/PressableBase";
import CurrencyBalanceIcon from "@components/accessories/CurrencyBalanceIcon";
import NetworkCurrencySymbol from "@components/accessories/NetworkCurrencySymbol";
import i18n from "@i18n/i18n";
import { trimHelper } from "@utils/helpers/chat/chat.helper";
import { getColorOpacity } from "@utils/helpers/global/global";
import { Defs, LinearGradient, Stop } from "react-native-svg";
import SwitchSelector from "react-native-switch-selector";

interface Props {
    amount?: string;
    balance?: number;
    title?: string;
    color?: string;
    symbol?: string;
    subtitle?: string;
    style?: StyleSheet;
    currency?: Currency;
    fiatCurrency?: FiatCurrency;
}

interface State {
    title: string;
    color: string;
    switch: boolean;
    price: Array<any>;
}

const { t } = i18n;

export default class CurrencyBalance extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            title: props.title,
            color: props.color ? props.color : colors.text,
            switch: false,
            price: this.props.currency.getPriceHistoryDaily().map((p) => p.price),
        };
        this.switchCurrency = this.switchCurrency.bind(this);
    }

    componentDidUpdate(props) {
        if (props != this.props) {
            this.setState({
                title: this.props.title,
                color: this.props.color ? props.color : colors.text,
            });
        }
    }

    switchCurrency = () => {
        this.setState(
            {
                switch: !this.state.switch,
                price: [],
            },
            () =>
                this.setState({
                    price: this.state.switch
                        ? this.props.currency.getPriceHistoryWeekly().map((p) => p.price)
                        : this.props.currency.getPriceHistoryDaily().map((p) => p.price),
                })
        );
    };

    render() {
        return (
            <View style={styles.container}>
                <View
                    style={{
                        position: "absolute",
                        width: "110%",
                        marginBottom: -60,
                        bottom: 0,
                        overflow: "hidden",
                        zIndex: -9999,
                    }}
                >
                    <AreaChart
                        style={{ height: 130, marginTop: -5, marginHorizontal: -10 }}
                        data={this.state.price}
                        contentInset={{ top: 10, bottom: 35 }}
                        svg={{
                            stroke: this.props.currency.getColor(),
                            strokeOpacity: this.state.price?.every((value) => value === this.state.price[0]) ? 0 : 1,
                            strokeWidth: 1,
                            fill: "url(#gradient)",
                        }}
                    >
                        <Defs>
                            <LinearGradient
                                id={"gradient"}
                                x1={"0%"}
                                y1={"0%"}
                                x2={"0%"}
                                y2={"100%"}
                                gradientUnits="userSpaceOnUse"
                            >
                                <Stop
                                    offset={"0%"}
                                    stopColor={this.props.currency.getColor()}
                                    stopOpacity={Platform.OS == "android" ? 0.5 : 0.6}
                                />
                                <Stop
                                    offset={Platform.OS == "android" ? "80%" : "90%"}
                                    stopColor={colors.gradientTo}
                                    stopOpacity={0}
                                />
                            </LinearGradient>
                        </Defs>
                    </AreaChart>
                </View>

                {this.props.currency && (
                    <Row style={{ width: "100%", justifyContent: "center", height: 30 }}>
                        <View
                            style={{
                                position: "absolute",
                                left: 0,
                                justifyContent: "center",
                                top: Platform.OS == "web" ? 5 : 0,
                            }}
                        >
                            <View style={{ opacity: 0.9 }}>
                                <RegularText
                                    align="center"
                                    fontSize={12}
                                    style={{
                                        backgroundColor: colors.tertiaryShadow,
                                        paddingHorizontal: 8,
                                        paddingVertical: 4,
                                        borderRadius: settings.cardRadius,
                                        overflow: "hidden",
                                    }}
                                >
                                    {trimHelper(this.props.currency.getPName().toUpperCase(), 20)}
                                </RegularText>
                            </View>

                            <NetworkCurrencySymbol
                                containerStyles={{ alignItems: "flex-start", marginTop: 5 }}
                                styles={{
                                    minWidth: 45,
                                    marginLeft: 2,
                                    paddingVertical: 4,
                                    paddingHorizontal: 8,
                                }}
                                fontSize={10}
                                currency={this.props.currency}
                            ></NetworkCurrencySymbol>
                        </View>

                        <View
                            style={{
                                position: "absolute",
                                right: 0,
                                top: Platform.OS == "web" ? 5 : 0,
                                alignItems: "flex-end",
                                justifyContent: "flex-end",
                            }}
                        >
                            {this.props.currency.getFiat() !== 0 && (
                                <CurrencyBalanceIcon
                                    style={{
                                        backgroundColor: colors.tertiaryShadow,
                                        paddingHorizontal: 6,
                                        paddingVertical: 4,
                                        borderRadius: 16,
                                        overflow: "hidden",
                                    }}
                                    amount={this.props.fiatCurrency.fiatFormat(this.props.currency.getFiat())}
                                    fiatCurrency={this.props.fiatCurrency}
                                />
                            )}

                            {this.state.switch && (
                                <View>
                                    {getPercentagePriceVariationColor(this.props.currency.getPriceHistoryWeekly()) && (
                                        <BoldText
                                            color={getPercentagePriceVariationColor(
                                                this.props.currency.getPriceHistoryWeekly()
                                            )}
                                            align="right"
                                            fontSize={12}
                                            style={{ lineHeight: 18, marginTop: 2, paddingHorizontal: 8 }}
                                        >
                                            {getPercentagePriceVariationDaily(
                                                this.props.currency.getPriceHistoryWeekly()
                                            )}
                                        </BoldText>
                                    )}
                                </View>
                            )}

                            {!this.state.switch && (
                                <View>
                                    {getPercentagePriceVariationColor(this.props.currency.getPriceHistoryDaily()) && (
                                        <BoldText
                                            color={getPercentagePriceVariationColor(
                                                this.props.currency.getPriceHistoryDaily()
                                            )}
                                            align="right"
                                            fontSize={12}
                                            style={{ lineHeight: 18, marginTop: 2, paddingHorizontal: 8 }}
                                        >
                                            {getPercentagePriceVariationDaily(
                                                this.props.currency.getPriceHistoryDaily()
                                            )}
                                        </BoldText>
                                    )}
                                </View>
                            )}
                        </View>
                    </Row>
                )}
                {Number(this.props.balance) < 0 ? (
                    <RegularText
                        style={{
                            textAlign: "center",
                            backgroundColor: getColorOpacity(colors.grey, 0.6),
                            borderRadius: 8,
                            overflow: "hidden",
                            paddingHorizontal: 10,
                            paddingVertical: 2,
                        }}
                        fontSize={12}
                        color={colors.highlightedText}
                    >
                        {t("inactive")}
                    </RegularText>
                ) : (
                    <BoldText
                        style={{ marginTop: 15 }}
                        fixedWidth={300}
                        align="center"
                        fontSize={35}
                        color={colors.text}
                    >
                        {this.props.amount}
                    </BoldText>
                )}
                {this.props.currency.getFiat() !== 0 && Number(this.props.currency.getFiatTotalBalance()) >= 0 && (
                    <CurrencyBalanceIcon
                        style={styles.fiatBalance}
                        fiatCurrency={this.props.fiatCurrency}
                        balance={this.props.currency.getFiatTotalBalance() || 0}
                    ></CurrencyBalanceIcon>
                )}

                <Row
                    style={{
                        justifyContent: "center",
                        alignItems: "center",
                        alignContent: "center",
                        position: "absolute",
                        marginBottom: -75,
                        bottom: 0,
                        zIndex: 9999,
                        // width: "100%",
                    }}
                >
                    <View style={{ position: "relative" }}>
                        <SwitchSelector
                            options={[
                                { label: trimHelper(t("daily"), 12, true), value: 1 },
                                {
                                    label: trimHelper(t("weekly"), 12, true),
                                    value: 0,
                                },
                            ]}
                            initial={this.state.switch ? 1 : 0}
                            value={this.state.switch ? 1 : 0}
                            height={35}
                            style={{
                                zIndex: 9999,
                                width: 200,
                                marginHorizontal: "auto",
                                alignSelf: "center",
                            }}
                            onPress={() => null}
                            textColor={colors.text}
                            selectedColor={colors.white}
                            buttonColor={colors.secondary}
                            borderColor={colors.secondary}
                            animationDuration={250}
                            backgroundColor={colors.shadow}
                        />
                        <PressableBase
                            onPress={this.switchCurrency}
                            style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 0, zIndex: 99999 }}
                        ></PressableBase>
                    </View>
                </Row>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        marginTop: Platform.OS == "web" ? 22 : 0,
        marginBottom: 98,
        alignItems: "center",
    },
    center: {
        flexDirection: "row",
        borderRadius: 15,
        width: 250,
        alignContent: "center",
        alignItems: "center",
        justifyContent: "center",
    },
    currencySymbol: {
        marginLeft: -20,
    },
    fiatBalance: {
        marginTop: 8,
        backgroundColor: colors.tertiaryShadow,
        paddingHorizontal: 8,
        paddingVertical: 6,
        borderRadius: 16,
        overflow: "hidden",
    },
});
